import type { InvestmentId, Project, ScenarioId } from '@finsim/model';
import { combine, createEvent, createStore, sample } from 'effector';
import { $project, projectClosed, projectShown } from '../project';

/** Main-canvas views, in tab order (keys 1–8). */
export const VIEWS = [
  'overview',
  'cashFlow',
  'balance',
  'debt',
  'risk',
  'compare',
  'ledger',
  'returns',
] as const;
export type View = (typeof VIEWS)[number];

/** The views a project shows (tabs, number keys, palette): Returns only once it has an investment. */
export function visibleViews(project: Pick<Project, 'investments'> | null): readonly View[] {
  return project && project.investments.length > 0 ? VIEWS : VIEWS.filter((v) => v !== 'returns');
}

/** The investment the Returns view shows (only it is analysed); the first one by default. */
export const investmentSelected = createEvent<InvestmentId>();
const $selectedInvestment = createStore<InvestmentId | null>(null)
  .on(investmentSelected, (_, id) => id)
  .reset(projectShown, projectClosed);
export const $shownInvestment = combine(
  $project,
  $selectedInvestment,
  (project, id): InvestmentId | null =>
    project?.investments.find((i) => i.id === id)?.id ?? project?.investments[0]?.id ?? null,
);

export const VALUE_MODES = ['nominal', 'real'] as const;
export type ValueMode = (typeof VALUE_MODES)[number];

export const SIM_MODES = ['deterministic', 'monteCarlo'] as const;
export type SimMode = (typeof SIM_MODES)[number];

export const viewChanged = createEvent<View>();
export const valueModeChanged = createEvent<ValueMode>();
export const valueModeToggled = createEvent();
export const simModeChanged = createEvent<SimMode>();
export const simModeToggled = createEvent();

export const $view = createStore<View>('overview').on(viewChanged, (_, view) => view);

// A view the open project doesn't show (Returns after undo, a `?view=` link, another project)
// falls back to the overview.
sample({
  clock: combine(
    $view,
    $project,
    (view, project) => !project || visibleViews(project).includes(view),
  ),
  filter: (shown) => !shown,
  fn: (): View => 'overview',
  target: viewChanged,
});

export const $valueMode = createStore<ValueMode>('nominal')
  .on(valueModeChanged, (_, mode) => mode)
  .on(valueModeToggled, (mode) => (mode === 'nominal' ? 'real' : 'nominal'));

export const $simMode = createStore<SimMode>('deterministic')
  .on(simModeChanged, (_, mode) => mode)
  .on(simModeToggled, (mode) => (mode === 'monteCarlo' ? 'deterministic' : 'monteCarlo'));

/** Visible window of month indexes (brush zoom), shared by every view; null = whole horizon. */
export type TimeRange = readonly [number, number];
export const timeRangeChanged = createEvent<TimeRange | null>();
export const $timeRange = createStore<TimeRange | null>(null)
  .on(timeRangeChanged, (_, range) => range)
  .reset(projectShown, projectClosed);

/**
 * The compare set, stored as the variants left out of the charts (so new variants show up).
 * Toggled from the variant tree and chart legends; synced to the URL as `cmp`. Not saved.
 */
export const scenarioVisibilityToggled = createEvent<ScenarioId>();
export const hiddenScenariosChanged = createEvent<ReadonlySet<ScenarioId>>();
export const $hiddenScenarios = createStore<ReadonlySet<ScenarioId>>(new Set())
  .on(scenarioVisibilityToggled, (set, id) => {
    const next = new Set(set);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  })
  .on(hiddenScenariosChanged, (_, set) => set)
  .reset(projectClosed);
